import React, { useRef, useState, useEffect } from 'react';

const Header = ({ activeSection, onSwitchSection, language, onSetLanguage }) => {
  const navItems = ['about', 'pentesting', 'education', 'projects', 'contact'];

  const translations = {
    de: { 
      nav_about: "Über mich", 
      nav_pentesting: "Pentesting", 
      nav_education: "Ausbildung", 
      nav_projects: "Projekte", 
      nav_contact: "Kontakt",
      role: "Junior Penetration Tester",
      contact_title: "Kontakt",
      certs_title: "Zertifizierungen",
      running: "laufend"
    },
    en: { 
      nav_about: "About Me", 
      nav_pentesting: "Pentesting", 
      nav_education: "Education", 
      nav_projects: "Projects", 
      nav_contact: "Contact",
      role: "Junior Penetration Tester",
      contact_title: "Contact",
      certs_title: "Certifications",
      running: "running"
    }
  };

  const navRef = useRef(null);
  const [indicatorStyle, setIndicatorStyle] = useState({ left: 0, width: 0, opacity: 0 });
  const [isAnimated, setIsAnimated] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    const updateIndicator = () => {
      if (!navRef.current) return;
      const activeEl = navRef.current.querySelector('.nav-link.active');
      if (activeEl) {
        setIndicatorStyle({
          left: activeEl.offsetLeft,
          width: activeEl.offsetWidth,
          opacity: 1
        });
      }
    };

    updateIndicator();

    const timer = setTimeout(() => {
      setIsAnimated(true);
    }, 50);

    if (document.fonts?.ready) {
      document.fonts.ready.then(updateIndicator);
    }

    window.addEventListener('resize', updateIndicator);
    return () => {
      clearTimeout(timer);
      window.removeEventListener('resize', updateIndicator);
    };
  }, [activeSection, language]);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        setMobileMenuOpen(false);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  // Prevent background scrolling when mobile menu is open
  useEffect(() => {
    if (mobileMenuOpen) {
      document.body.classList.add('menu-open');
    } else {
      document.body.classList.remove('menu-open');
    }
    return () => document.body.classList.remove('menu-open');
  }, [mobileMenuOpen]);

  const t = translations[language];

  return (
    <header className="header">
      <div className="header-title-wrap">
        <h1>Elias Meisl</h1>
      </div>

      <nav className="desktop-nav" ref={navRef}>
        {navItems.map(item => (
          <a 
            key={item}
            href={`#${item}`} 
            className={`nav-link ${activeSection === item ? 'active' : ''}`} 
            onClick={(e) => {
              e.preventDefault();
              onSwitchSection(item);
            }}
          >
            {t[`nav_${item}`]}
          </a>
        ))}
        <span 
          className={`nav-indicator ${isAnimated ? 'animate' : ''}`}
          style={{
            transform: `translateX(${indicatorStyle.left}px)`,
            width: `${indicatorStyle.width}px`,
            opacity: indicatorStyle.opacity
          }}
        />
      </nav>

      <div className="language-switcher desktop-lang">
        <a 
          href="#de" 
          className={`lang-link ${language === 'de' ? 'active' : ''}`} 
          onClick={(e) => { e.preventDefault(); onSetLanguage('de'); }}
        >
          DE
        </a>
        <span>/</span>
        <a 
          href="#en" 
          className={`lang-link ${language === 'en' ? 'active' : ''}`} 
          onClick={(e) => { e.preventDefault(); onSetLanguage('en'); }}
        >
          EN
        </a>
      </div>

      <button 
        type="button"
        className={`mobile-menu-btn ${mobileMenuOpen ? 'open' : ''}`}
        onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
        aria-label="Toggle navigation menu"
        aria-expanded={mobileMenuOpen}
      >
        <span className="hamburger-bar"></span>
        <span className="hamburger-bar"></span>
        <span className="hamburger-bar"></span>
      </button>

      {/* Mobile Drawer Overlay */}
      <div 
        className={`mobile-drawer-overlay ${mobileMenuOpen ? 'open' : ''}`}
        onClick={() => setMobileMenuOpen(false)}
        aria-hidden="true"
      />

      {/* Mobile Drawer Panel */}
      <aside className={`mobile-drawer ${mobileMenuOpen ? 'open' : ''}`} aria-hidden={!mobileMenuOpen}>
        <div className="mobile-drawer-header">
          <div className="mobile-lang-switcher">
            <button 
              type="button"
              className={`mobile-lang-btn ${language === 'de' ? 'active' : ''}`}
              onClick={() => onSetLanguage('de')}
            >
              DE
            </button>
            <span className="mobile-lang-divider">/</span>
            <button 
              type="button"
              className={`mobile-lang-btn ${language === 'en' ? 'active' : ''}`}
              onClick={() => onSetLanguage('en')}
            >
              EN
            </button>
          </div>
        </div>

        <nav className="mobile-nav-links">
          {navItems.map(item => (
            <button
              key={item}
              type="button"
              className={`mobile-nav-item ${activeSection === item ? 'active' : ''}`}
              onClick={() => {
                onSwitchSection(item);
                setMobileMenuOpen(false);
              }}
            >
              <span className="mobile-nav-dot"></span>
              <span className="mobile-nav-text">{t[`nav_${item}`]}</span>
              {activeSection === item && <span className="mobile-active-tag">Active</span>}
            </button>
          ))}
        </nav>

        <div className="mobile-drawer-footer">
          <div className="mobile-role-pill">
            <strong>{t.role}</strong>
          </div>

          <div className="mobile-drawer-section">
            <h4>{t.contact_title}</h4>
            <ul className="mobile-drawer-list">
              <li>
                <a href="https://www.linkedin.com/in/elias-meisl-563050329" target="_blank" rel="noopener noreferrer">
                  LinkedIn: <strong>Elias Meisl</strong>
                </a>
              </li>
              <li>
                <a href="mailto:eliasmeisl02@gmail.com">
                  E-Mail: <strong>eliasmeisl02@gmail.com</strong>
                </a>
              </li>
              <li>
                <span>{language === 'de' ? 'Wien, Österreich' : 'Vienna, Austria'}</span>
              </li>
            </ul>
          </div>

          <div className="mobile-drawer-section">
            <h4>{t.certs_title}</h4>
            <ul className="mobile-drawer-list">
              <li>
                <a href="https://www.credly.com/badges/360877f7-0b60-4166-b685-922264944013/linked_in_profile" target="_blank" rel="noopener noreferrer">
                  CISCO Ethical Hacker &rarr;
                </a>
              </li>
              <li>
                <span>HTB CPTS [{t.running}]</span>
              </li>
            </ul>
          </div>
        </div>
      </aside>
    </header>
  );
};

export default Header;